import * as _ from 'underscore';
import { ILockUnlockProjectPermissionsScope } from '../directives/tbLockUnlockProjectPermissions';
import { CommonErrorCode } from '../helpers/ErrorCodes';
import { LockUnlockPermissionsState } from '../helpers/LockStatusHelper';
import { L10n } from '../L10n/L10n';
import { ILongRunningAction } from '../models/ILongRunningAction';
import { IProjectBase, IProjectInfo } from '../models/IProjectInfo';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { Projects } from '../resources/Projects';
import { IActionResult } from '../ServerApi/types/IActionResult';
import { IError } from '../ServerApi/types/IError';
import { IEmptyResult, IResult } from '../ServerApi/types/IResponse';
import { LongRunningActionService } from './LongRunningActionService';
import { ErrorReason } from './ServerService';
import { IToastOptions, ToasterService, ToastType } from './ToasterService';

export class SetProjectControlledPermissionsAction {

  public static $inject = ['$translate', 'ToasterService', 'Projects', 'LongRunningActionService'];

  constructor(
    private $translate: ng.translate.ITranslateService,
    private toaster: ToasterService,
    private Projects: Projects,
    private longRunningActionService: LongRunningActionService,
    private scope: ILockUnlockProjectPermissionsScope) { }

  public execute(projects: Set<IProjectInfo>, updatedPermissionSource: LockUnlockPermissionsState[],
    scope: ILockUnlockProjectPermissionsScope): ng.IPromise<IResult> {

    this.scope = scope;
    if (_.first(updatedPermissionSource) === LockUnlockPermissionsState.Locked) {
      return this.lockProjectPermissions(projects);
    } else {
      return this.unlockProjectPermissions(projects);
    }
  }

  private lockProjectPermissions(projects: Set<IProjectInfo>): ng.IPromise<IResult> {

    const projIds: string[] = projects.pluck('id');

    return this.Projects.setProjectControlledPermissions(projIds)
      .then((result: IEmptyResult) => {
        let toast: IToastOptions;
        if (result.errors && result.errors.length) {
          toast = {
            type: ToastType.Error,
            text: this.errorMessageFor(result.errors[0], projects.first()),
          };
        } else {
          const permission_source: string = this.$translate.instant('lockProjectPermissionsChangeSuccess_locked',
            { PROJECT_COUNT : projects.size() });
          toast = {
            type: ToastType.Info,
            text: this.$translate.instant('lockProjectPermissionsChangeSuccess',
              { PROJECT_COUNT: projects.size(),
                PROJECT_NAME: L10n.elideIfLong(projects.first().name),
                PERMISSION_SOURCE: permission_source
              }, L10n.Interpolations.Count),
          };
        }
        this.toaster.pop(toast);
        return result;
      }
    );
  }

  private unlockProjectPermissions(projects: Set<IProjectInfo>): ng.IPromise<IResult> {

    const projIds: string[] = projects.pluck('id');

    const permissionChangeSuccess = (result: ILongRunningAction) => {
      if (result && result.succeeded === false) {
        permissionsChangeFail(ErrorReason.Unknown);
      } else {
        this.scope.percentComplete = result.percentComplete;
        const permission_source: string = this.$translate.instant('lockProjectPermissionsChangeSuccess_unlocked',
          { PROJECT_COUNT : projects.size() });
        const toast: IToastOptions = {
          type: ToastType.Info,
          text: this.$translate.instant('lockProjectPermissionsChangeSuccess',
            { PROJECT_COUNT: projects.size(),
              PROJECT_NAME: L10n.elideIfLong(projects.first().name),
              PERMISSION_SOURCE: permission_source
            }, L10n.Interpolations.Count),
        };
        this.toaster.pop(toast);
        this.scope.busyUnlocking = false;
      }

      return result.response;
    };

    const permissionsChangeFail = (reason: ErrorReason) => {
      this.scope.busyUnlocking = false;
      const toast: IToastOptions = {
        type: ToastType.Error,
        text: this.$translate.instant('errorUnknown'),
      };
      this.toaster.pop(toast);
    };

    const notifyProgress = (action: ILongRunningAction) => {
      this.scope.busyUnlocking = true;
      this.scope.percentComplete = action.percentComplete;
    };

    return this.Projects.setContentControlledPermissions(projIds)
      .then((result: IActionResult) => {
        const progress = this.longRunningActionService.trackAsyncJobProgress(result.actionId);
        return progress.promise.then(permissionChangeSuccess, permissionsChangeFail, notifyProgress);
      }
    );
  }

  private errorMessageFor(error: IError, project: IProjectBase) {
    switch (error.code) {
      case CommonErrorCode.ProjectNotFound:
        return this.$translate.instant('errorProjectNotFound', { PROJECT_NAME: L10n.elideIfLong(project.name) });
      default:
        return this.$translate.instant('errorUnknown');
    }
  }

}

Module.instance().service('SetProjectControlledPermissionsAction',
    SetProjectControlledPermissionsAction);
